/**
 * Command.ts - Generic Command Pattern
 *
 * All game actions are abstracted into Command objects.
 * This enables recording, replaying, and serialization of player actions.
 */

export interface ICommand<TType extends string = string, TPayload = unknown> {
  type: TType;
  payload: TPayload;
  timestamp: number;
}

export class Command<TType extends string = string, TPayload = unknown>
  implements ICommand<TType, TPayload>
{
  public readonly timestamp: number;

  constructor(
    public readonly type: TType,
    public readonly payload: TPayload,
    timestamp?: number,
  ) {
    this.timestamp = typeof timestamp === 'number' ? timestamp : Date.now();
  }

  toJSON(): ICommand<TType, TPayload> {
    return {
      type: this.type,
      payload: this.payload,
      timestamp: this.timestamp,
    };
  }

  static fromJSON<TType extends string, TPayload>(
    obj: ICommand<TType, TPayload>,
  ): Command<TType, TPayload> {
    return new Command(obj.type, obj.payload, obj.timestamp);
  }
}

type Handler<TState, TCmd extends ICommand> = (state: TState, cmd: TCmd) => TState;

export class CommandExecutor<TState, TCmd extends ICommand> {
  private handlers = new Map<string, Handler<TState, TCmd>>();

  register<T extends TCmd>(type: T['type'], handler: Handler<TState, T>): void {
    this.handlers.set(type, handler as Handler<TState, TCmd>);
  }

  execute(cmd: TCmd, state: TState): TState {
    const handler = this.handlers.get(cmd.type);
    if (!handler) {
      console.warn(`[CommandExecutor] No handler for: ${cmd.type}`);
      return state;
    }
    return handler(state, cmd);
  }
}
